// Login.jsx - Works for Microsoft, Google, Apple, and all email domains

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import { useMsal } from "@azure/msal-react";
import { useDispatch } from "react-redux";
import { setEmail as setEmailAction } from "./userSlice"; // FIX: renamed on import to avoid collision with local useState setter
import { setSession, getSession } from "./utils/session";
import { loginRequest } from "./utils/msalConfig";

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { instance } = useMsal(); // MSAL client for Microsoft SSO
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Base URL
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api-project360-insights.haqdarshak.com';

  // Shown whenever a login attempt does not end in a server-verified session.
  // Deliberately generic — it must not reveal whether the email exists, the
  // password was wrong, or a supplied token was rejected.
  const _GENERIC_LOGIN_ERROR = "Invalid email or password.";

  // Function to log login to backend
  const logUserLogin = async (emailToLog) => {
    try {
      const response = await fetch(`${API_BASE_URL}/kpi/auth/log-login/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: emailToLog
        })
      });

      const data = await response.json();
      if (data.success) {
        console.log('Login logged successfully:', data);
      } else {
        console.error('Failed to log login:', data.error);
      }
    } catch (err) {
      console.error('Error logging login:', err);
      // Don't block login if logging fails
    }
  };

  // Fetch a fresh, single-use anti-forgery token before a login POST.
  //
  // The login endpoints are public (no session/JWT exists yet), so there's no
  // cookie for Django's CSRF machinery to attach to — this app doesn't use
  // session cookies at all. The backend instead hands out a short-lived token
  // here that must be echoed back in the login POST body; it's burned the
  // instant it's presented, so it can't be replayed (VAPT: "No CSRF Token
  // Protection on Login Page").
  const fetchLoginCsrfToken = async () => {
    const res = await fetch(`${API_BASE_URL}/api/auth/csrf-token/`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data && data.csrfToken ? data.csrfToken : null;
  };

  // Establish the session from a token the backend just returned.
  //
  // SECURITY: the login response body is NOT trusted. Anyone able to see the
  // traffic (a proxy such as Burp, or devtools) can rewrite a 401 into a
  // {"success": true, "email": "...", "token": "..."} body and replay a token
  // captured from an earlier session — the VAPT "Login Bypass" PoC. So:
  //
  //   * `success` / `message` / HTTP status from the body prove nothing; the
  //     only thing that counts is whether the SERVER accepts the token.
  //   * the account identity is read from the server's answer, never from
  //     `data.email`, so a swapped email cannot make the app show or act as
  //     somebody else.
  //
  // Returns the verified email, or null if the token is not genuine (forged,
  // expired, or belonging to a session that has since been logged out).
  const verifyTokenAndGetEmail = async (token) => {
    if (!token) return null;
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/verify/`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) return null;
      const verified = await res.json();
      return (verified && verified.email) ? String(verified.email).trim() : null;
    } catch (err) {
      // Fail closed — never grant a session on uncertainty.
      console.error('Token verification failed:', err);
      return null;
    }
  };

  // Shared tail of every login flow (email, Google, Microsoft): prove the token
  // server-side, then store the identity the server vouched for.
  const completeLogin = async (token) => {
    const verifiedEmail = await verifyTokenAndGetEmail(token);
    if (!verifiedEmail) return false;

    setSession({ email: verifiedEmail, token });
    dispatch(setEmailAction(verifiedEmail));
    await logUserLogin(verifiedEmail);
    navigate("/Project-Summary");
    return true;
  };

  // Handle email/password login.
  //
  // Note on the endpoint name: despite being called /api/microsoft-login/,
  // this backend view actually implements email+password auth (legacy
  // naming from before Google/Microsoft SSO existed) — Microsoft SSO itself
  // goes through exchangeMicrosoftToken → /api/google-login/ below.
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError("");

    if (!email || !password) {
      setError("Please enter both email and password");
      return;
    }

    setLoading(true);

    try {
      // Same anti-forgery step as the SSO flows: a fresh, single-use token
      // fetched immediately before the login POST (VAPT: "No CSRF Token
      // Protection on Login Page").
      const csrfToken = await fetchLoginCsrfToken();
      if (!csrfToken) {
        setError('Could not start a secure login session. Please try again.');
        setLoading(false);
        return;
      }

      // Credentials are sent in the POST body over HTTPS — never in the URL.
      const response = await fetch(`${API_BASE_URL}/api/microsoft-login/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ email, password, csrfToken }),
      });

      // Parse JSON safely
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
        setError('Server returned an invalid response. Please try again.');
        setLoading(false);
        return;
      }

      // A token is only a candidate until the server confirms it — see
      // completeLogin. A tampered response body cannot get past this.
      if (response.ok && data.success && data.token) {
        const ok = await completeLogin(data.token);
        if (!ok) {
          setError(_GENERIC_LOGIN_ERROR);
        }
      } else {
        // Show the backend's error (rate-limit message, generic bad-login
        // message, or expired CSRF token) rather than inventing our own.
        const errorMessage = data.error || data.message || "Login failed. Please try again.";
        setError(errorMessage);
      }
    } catch (err) {
      console.error('❌ Login error:', err);
      setError(`An error occurred: ${err.message || 'Please try again.'}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle Google SSO Login — the Google credential is verified server-side,
  // which then issues our own backend JWT. We no longer trust a client-decoded
  // Google token as proof of identity.
  const handleGoogleLogin = async (response) => {
    try {
      const csrfToken = await fetchLoginCsrfToken();
      if (!csrfToken) {
        alert('Could not start a secure login session. Please try again.');
        return;
      }

      const res = await fetch(`${API_BASE_URL}/api/google-login/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ credential: response.credential, csrfToken }),
      });

      const data = await res.json();

      if (res.ok && data.success && data.token) {
        const ok = await completeLogin(data.token);
        if (!ok) {
          alert('Google sign-in failed. Please try again.');
        }
      } else {
        alert(data.error || 'Google sign-in failed. Please try again.');
      }
    } catch (err) {
      console.error('Login error:', err);
      alert('Login failed. Please try again.');
    }
  };

  // Exchange a Microsoft Azure AD ID token for our backend JWT — mirrors the
  // Google flow. The token is verified server-side; we never trust a
  // client-decoded token as proof of identity. Used by the redirect handler.
  const exchangeMicrosoftToken = async (credential) => {
    const csrfToken = await fetchLoginCsrfToken();
    if (!csrfToken) {
      setError('Could not start a secure login session. Please try again.');
      return;
    }

    const res = await fetch(`${API_BASE_URL}/api/google-login/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({ credential, csrfToken }),
    });

    // Parse JSON safely — a non-JSON body means the backend errored (e.g. the
    // endpoint doesn't yet accept a Microsoft credential).
    let data;
    try {
      data = await res.json();
    } catch (parseErr) {
      console.error('Microsoft login: non-JSON response', parseErr);
      setError(`Server returned an invalid response (HTTP ${res.status}). The backend microsoft-login endpoint may not accept a Microsoft credential yet.`);
      return;
    }

    if (res.ok && data.success && data.token) {
      // Dashboard route is unchanged — same path as the Google flow.
      const ok = await completeLogin(data.token);
      if (!ok) {
        setError('Microsoft sign-in could not be verified. Please try again.');
      }
    } else {
      setError(data.error || data.message || `Microsoft sign-in failed (HTTP ${res.status}).`);
    }
  };

  // If a valid session already exists (e.g. after a refresh that dropped the
  // user onto the base URL), send them straight back into the app — to the
  // page they were last on, or the dashboard as a fallback. This does NOT fire
  // during a Microsoft redirect return: the session isn't set until the token
  // exchange below completes, so this stays a no-op until then.
  useEffect(() => {
    const session = getSession();
    if (session && session.email && session.token) {
      let last = null;
      try { last = sessionStorage.getItem("lastPath"); } catch { /* ignore */ }
      navigate(last && last !== "/" ? last : "/Project-Summary", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On return from the Microsoft sign-in page, MSAL delivers the auth result
  // here. We use the REDIRECT flow (not popup) because popup sign-in throws
  // `block_nested_popups` whenever the app runs inside a popup / embedded
  // browser context. Redirect navigates the whole window instead.
  useEffect(() => {
    let cancelled = false;
    const processRedirect = async () => {
      try {
        await instance.initialize();
        const result = await instance.handleRedirectPromise();
        if (!cancelled && result && result.idToken) {
          await exchangeMicrosoftToken(result.idToken);
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Microsoft redirect handling error:', err);
        setError(`Microsoft login error: ${err?.errorMessage || err?.message || 'Please try again.'}`);
      }
    };
    processRedirect();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle Microsoft SSO Login — starts the redirect flow. The whole window
  // navigates to Microsoft; on return the effect above completes the sign-in.
  const handleMicrosoftLogin = async () => {
    setError("");
    try {
      // MSAL v3+ requires initialize() before any auth call. Idempotent, so it
      // guards against the "uninitialized_public_client_application" crash.
      await instance.initialize();
      await instance.loginRedirect(loginRequest);
    } catch (err) {
      console.error('Microsoft login error:', err);
      setError(`Microsoft login error: ${err?.errorMessage || err?.message || 'Please try again.'}`);
    }
  };

  return (
    <div className="relative w-full h-screen flex items-center justify-center bg-pink-100">
      <div className="bg-pink-200 p-8 rounded-2xl shadow-lg w-96 text-center">
        <h1 className="text-xl font-bold text-gray-800">हक़दर्शक</h1>
        <h2 className="text-lg font-semibold text-gray-700 mt-2">Haqdarshak</h2>
        <p className="text-gray-600 mt-2">
          Welcome to Haqdarshak Analytics
        </p>

        {!showEmailLogin ? (
          <div className="mt-6 space-y-4">
            {/* SSO error message (Google / Microsoft) */}
            {error && (
              <div className="mb-2 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm text-left break-words">
                {error}
              </div>
            )}

            {/* Google SSO Login Button - UNCHANGED */}
            <div className="w-full justify-center ">
              <GoogleLogin
                onSuccess={handleGoogleLogin}
                onError={() => alert("Google Sign-in failed")}
              />
            </div>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-pink-200 text-gray-500">Or</span>
              </div>
            </div>

            {/* Microsoft SSO Login Button */}
            <button
              onClick={handleMicrosoftLogin}
              className="w-full bg-white hover:bg-gray-50 text-gray-800 font-semibold py-2 border border-gray-300 rounded-lg shadow-sm transition-all duration-200 flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
              </svg>
              Sign in with Microsoft
            </button>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-pink-200 text-gray-500">Or</span>
              </div>
            </div>

            <button
              onClick={() => {
                setShowEmailLogin(true);
                setError("");
              }}
              className="w-full bg-white hover:bg-gray-50 text-gray-800 font-semibold py-2 px-4 border border-gray-300 rounded-lg shadow-sm transition-all duration-200 flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
              </svg>
              Sign in with Email
            </button>
          </div>
        ) : (
          <div className="mt-6">
            <button
              onClick={() => {
                setShowEmailLogin(false);
                setError("");
                setEmail("");
                setPassword("");
              }}
              className="float-left text-sm text-gray-600 hover:text-gray-800 flex items-center gap-1 mb-4"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back
            </button>

            <h3 className="text-lg font-semibold text-gray-700 mb-4">Sign in with Email</h3>

            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleEmailLogin} className="space-y-4">
              <div>
                <input
                  type="email"
                  placeholder="Enter Your Email Address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-400 focus:border-transparent transition-all duration-200"
                  disabled={loading}
                  required
                />
              </div>

              <div>
                <input
                  type="password"
                  placeholder="Enter Your Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-400 focus:border-transparent transition-all duration-200"
                  disabled={loading}
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white font-semibold py-2 px-4 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </>
                ) : (
                  "Sign In"
                )}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;